import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Trash2, BookOpenCheck, Layers, Users, User, ChevronRight, AlertTriangle, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import api from '../../api';
import { SCHOOL } from '../../constants/testIds';

export default function ClassesPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [detailClass, setDetailClass] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // class to delete

  const load = async () => {
    setLoading(true);
    try {
      const [cr, sr] = await Promise.all([api.get('/academics/classes'), api.get('/academics/subjects')]);
      setClasses(cr.data || []);
      setSubjects(sr.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const confirmDeleteClass = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/academics/classes/${deleteConfirm.id}`);
      toast.success('Class deleted');
      setDeleteConfirm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.detail || 'Failed to delete class');
      setDeleteConfirm(null);
    }
  };

  const deleteSubject = async (id) => {
    try { await api.delete(`/academics/subjects/${id}`); toast.success('Subject deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">Classes & Subjects</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your school's academic structure</p>
      </div>

      <Tabs defaultValue="classes">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="classes" className="rounded-lg text-sm">Classes ({classes.length})</TabsTrigger>
          <TabsTrigger value="subjects" className="rounded-lg text-sm">Subjects ({subjects.length})</TabsTrigger>
        </TabsList>

        {/* Classes Tab */}
        <TabsContent value="classes" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowClassModal(true)} className="bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid={SCHOOL.addClassBtn}>
              <Plus className="w-4 h-4 mr-2" /> Add Class
            </Button>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-36 bg-slate-200 rounded-2xl animate-pulse" />)}</div>
          ) : classes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center" data-testid={SCHOOL.classesList}>
              <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600">No classes yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your school's class structure</p>
              <Button size="sm" className="mt-4 bg-[#516EFA] text-white" onClick={() => setShowClassModal(true)}>Add First Class</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={SCHOOL.classesList}>
              {classes.map((c) => (
                <div
                  key={c.id}
                  className="bg-white rounded-2xl p-5 border border-slate-100 hover:border-[#516EFA]/40 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => setDetailClass(c)}
                  data-testid={`class-card-${c.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-xl bg-[#eef1ff] flex items-center justify-center">
                      <Layers className="w-5 h-5 text-[#516EFA]" />
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(c); }}
                      className="text-slate-300 hover:text-red-400 transition-colors"
                      data-testid={`delete-class-${c.id}`}
                      title="Delete class"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="font-outfit font-bold text-slate-900 mt-3 text-lg">{c.name}</p>
                  <p className="text-xs text-slate-500 capitalize mt-0.5">{c.level} level</p>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-slate-600 flex items-center gap-1">
                      <Users className="w-3 h-3" />{c.student_count || 0} students
                    </span>
                    <div className="flex gap-1">
                      {(c.sections || ['A']).map(s => (
                        <Badge key={s} className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-[#516EFA] opacity-0 group-hover:opacity-100 transition-opacity">View details →</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Subjects Tab */}
        <TabsContent value="subjects" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowSubjectModal(true)} className="bg-[#14C8C4] hover:bg-[#12b0ad] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Subject
            </Button>
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-2xl animate-pulse" />)}</div>
          ) : subjects.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600">No subjects yet</p>
              <Button size="sm" className="mt-4 bg-[#14C8C4] text-white" onClick={() => setShowSubjectModal(true)}>Add First Subject</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {subjects.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl p-5 border border-slate-100 hover:border-[#14C8C4]/30 hover:shadow-md transition-all duration-200">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                      <BookOpenCheck className="w-5 h-5 text-[#14C8C4]" />
                    </div>
                    <button onClick={() => deleteSubject(s.id)} className="text-slate-300 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="font-semibold text-slate-800 mt-3">{s.name}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge className="bg-teal-50 text-teal-700 text-xs">{s.code}</Badge>
                    {s.is_compulsory && <Badge className="bg-[#eef1ff] text-[#516EFA] text-xs">Compulsory</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showClassModal && <ClassModal onClose={() => setShowClassModal(false)} onSuccess={() => { setShowClassModal(false); load(); }} />}
      {showSubjectModal && <SubjectModal onClose={() => setShowSubjectModal(false)} onSuccess={() => { setShowSubjectModal(false); load(); }} />}

      {/* Class Detail Panel */}
      {detailClass && (
        <ClassDetailPanel
          cls={detailClass}
          onClose={() => setDetailClass(null)}
          onNavigateStudents={(classId) => {
            setDetailClass(null);
            navigate(`/school/students?class_id=${classId}`);
          }}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete {deleteConfirm?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the class. Classes with active students cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteClass}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-class"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Class Detail Panel ────────────────────────────────────────────────────────

function ClassDetailPanel({ cls, onClose, onNavigateStudents }) {
  const [students, setStudents] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches = [
      api.get(`/students?class_id=${cls.id}&limit=200`)
        .then(r => setStudents(r.data || []))
        .catch(() => {}),
    ];
    if (cls.class_teacher_id) {
      fetches.push(
        api.get(`/teachers/${cls.class_teacher_id}`)
          .then(r => { if (r.success) setTeacher(r.data); })
          .catch(() => {})
      );
    }
    Promise.all(fetches).finally(() => setLoading(false));
  }, [cls.id, cls.class_teacher_id]);

  const sections = cls.sections || ['A'];

  // Group students by section
  const sectionMap = {};
  sections.forEach(s => { sectionMap[s] = []; });
  const unassigned = [];
  students.forEach(st => {
    const sec = st.section;
    if (sec && sectionMap[sec] !== undefined) {
      sectionMap[sec].push(st);
    } else if (sec && !sectionMap[sec]) {
      sectionMap[sec] = [st];
    } else {
      unassigned.push(st);
    }
  });

  const initials = (st) => `${st.first_name?.[0] || ''}${st.last_name?.[0] || ''}`.toUpperCase();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="class-detail-panel">
        {/* Header */}
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#eef1ff] flex items-center justify-center">
              <Layers className="w-6 h-6 text-[#516EFA]" />
            </div>
            <div>
              <DialogTitle className="font-outfit text-xl">{cls.name}</DialogTitle>
              <p className="text-sm text-slate-500 capitalize mt-0.5">
                {cls.level} level · {cls.student_count || students.length} students · Capacity {cls.capacity || 40}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-5 pr-1">
          {/* Class Teacher */}
          <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3" data-testid="class-teacher-info">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-blue-500 uppercase tracking-wide">Class Teacher</p>
              {loading ? (
                <div className="h-4 w-32 bg-blue-200 rounded animate-pulse mt-1" />
              ) : teacher ? (
                <p className="font-semibold text-slate-800">{teacher.first_name} {teacher.last_name}</p>
              ) : (
                <p className="text-slate-500 text-sm italic">No class teacher assigned</p>
              )}
            </div>
          </div>

          {/* Sections / Arms */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : (
            <>
              {sections.map(section => {
                const sStudents = sectionMap[section] || [];
                return (
                  <div key={section} data-testid={`section-${section}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-full bg-[#eef1ff] flex items-center justify-center text-[#516EFA] font-bold text-sm">{section}</div>
                      <h3 className="font-semibold text-slate-800">{cls.name} {section}</h3>
                      <Badge className="bg-slate-100 text-slate-600 text-[11px]">{sStudents.length} students</Badge>
                    </div>
                    {sStudents.length === 0 ? (
                      <p className="text-slate-400 text-sm pl-9 py-2">No students assigned to this section</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {sStudents.slice(0, 8).map(st => (
                          <div key={st.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="w-7 h-7 rounded-full bg-[#eef1ff] text-[#516EFA] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                              {initials(st)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{st.first_name} {st.last_name}</p>
                              <p className="text-[10px] text-slate-400 truncate">{st.admission_number}</p>
                            </div>
                          </div>
                        ))}
                        {sStudents.length > 8 && (
                          <div className="p-2 text-sm text-slate-400 flex items-center">
                            +{sStudents.length - 8} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unassigned students (no section) */}
              {unassigned.length > 0 && sections.length > 1 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-slate-400" />
                    <h3 className="font-semibold text-slate-600 text-sm">Without section assignment</h3>
                    <Badge className="bg-slate-100 text-slate-500 text-[11px]">{unassigned.length}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {unassigned.slice(0, 6).map(st => (
                      <div key={st.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {initials(st)}
                        </div>
                        <p className="text-sm font-medium text-slate-700 truncate">{st.first_name} {st.last_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All students if only one section (or no sections) */}
              {sections.length <= 1 && unassigned.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-slate-400" />
                    <h3 className="font-semibold text-slate-600 text-sm">All Students</h3>
                    <Badge className="bg-slate-100 text-slate-500 text-[11px]">{unassigned.length}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {unassigned.slice(0, 8).map(st => (
                      <div key={st.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-7 h-7 rounded-full bg-[#eef1ff] text-[#516EFA] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {initials(st)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{st.first_name} {st.last_name}</p>
                          <p className="text-[10px] text-slate-400 truncate">{st.admission_number}</p>
                        </div>
                      </div>
                    ))}
                    {unassigned.length > 8 && (
                      <div className="p-2 text-sm text-slate-400">+{unassigned.length - 8} more</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pt-4 border-t border-slate-100">
          <Button
            onClick={() => onNavigateStudents(cls.id)}
            className="w-full bg-[#516EFA] hover:bg-[#4058e8] text-white"
            data-testid="see-all-students-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            See All Students in {cls.name}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Class Create Modal ────────────────────────────────────────────────────────

function ClassModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', level: 'secondary', sections: ['A'], capacity: 40 });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Class name is required'); return; }
    setLoading(true);
    try {
      await api.post('/academics/classes', form);
      toast.success('Class created');
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to create class'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-outfit">Add New Class</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Class Name</Label><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="JSS 1, Primary 3, SS 2..." className="h-10" /></div>
          <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Level</Label>
            <Select value={form.level} onValueChange={v => update('level', v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="nursery">Nursery</SelectItem><SelectItem value="primary">Primary</SelectItem><SelectItem value="secondary">Secondary</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Capacity</Label><Input type="number" value={form.capacity} onChange={e => update('capacity', parseInt(e.target.value))} className="h-10" /></div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#516EFA] hover:bg-[#4058e8] text-white">{loading ? 'Creating...' : 'Create Class'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Subject Create Modal ──────────────────────────────────────────────────────

function SubjectModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', is_compulsory: true });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Subject name is required'); return; }
    setLoading(true);
    try {
      await api.post('/academics/subjects', form);
      toast.success('Subject created');
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Failed to create subject'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-outfit">Add New Subject</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Subject Name</Label><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Mathematics, English Language..." className="h-10" /></div>
          <div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">Code (optional)</Label><Input value={form.code} onChange={e => update('code', e.target.value)} placeholder="MTH, ENG..." className="h-10" /></div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#14C8C4] hover:bg-[#12b0ad] text-white">{loading ? 'Creating...' : 'Create Subject'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
